// src/components/templates/TemplateList.jsx
import React, { useMemo } from "react";
import {
    Box, Stack, Typography, Card, CardContent, Chip,
    IconButton, Tooltip, Divider, CardActions
} from "@mui/material";
import * as Lucide from "lucide-react";

function statusInfo(tpl) {
    const s = (tpl.status || "").toUpperCase();
    if (s === "COMPLETE") {
        return { label: "Completo", color: "success", icon: <Lucide.FileText size={18} /> };
    }
    return { label: "Pendente", color: "warning", icon: <Lucide.Hourglass size={18} /> };
}

function fmt(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleString();
}

export default function TemplateList({
                                         items = [],
                                         loading = false,
                                         filterText = "",
                                         onEdit,
                                         onOpenEditor,
                                         onDuplicate,
                                         onDelete,
                                         onExportPdf,
                                     }) {
    const filtered = useMemo(() => {
        const q = (filterText || "").trim().toLowerCase();
        if (!q) return items;

        return items.filter((t) => {
            const hay = [t.name, t.version, t.tag].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [items, filterText]);

    const summary = useMemo(() => {
        const total = filtered.length;
        const completos = filtered.filter((t) => (t.status || "").toUpperCase() === "COMPLETE").length;
        return { total, completos, pendentes: total - completos };
    }, [filtered]);

    if (loading) return <Typography color="text.secondary">Carregando…</Typography>;
    if (filtered.length === 0) {
        return <Typography color="text.secondary">Nenhum template encontrado.</Typography>;
    }

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                {summary.total} template(s) • {summary.completos} completo(s) • {summary.pendentes} pendente(s)
            </Typography>

            <Stack spacing={2}>
                {filtered.map((tpl) => {
                    const st = statusInfo(tpl);
                    const createdBy = tpl.createdBy || "admin";
                    const createdAt = tpl.createdAt || Date.now();
                    const updatedAt = tpl.updatedAt || tpl.createdAt || createdAt;

                    return (
                        <Card key={tpl.id} variant="outlined" sx={{ overflow: "hidden" }}>
                            <CardContent sx={{ pb: 1.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: "wrap" }}>
                                            <Typography variant="h6" noWrap>{tpl.name || "Sem nome"}</Typography>
                                            <Chip size="small" label={`v${tpl.version || "—"}`} />
                                            <Chip size="small" color={st.color} icon={st.icon} label={st.label} />
                                            {tpl.hasVariables && (
                                                <Chip size="small" variant="outlined" label="Tem variáveis" />
                                            )}
                                        </Stack>

                                        {/* ✅ Tag única */}
                                        {tpl.tag && (
                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                <Lucide.Tag size={18} style={{ opacity: 0.6 }} />
                                                <Chip size="small" label={tpl.tag} sx={{ mr: 0.5, mb: 0.5 }} />
                                            </Stack>
                                        )}

                                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 0.9, flexWrap: "wrap" }}>
                                            <Chip
                                                size="small"
                                                icon={<Lucide.User size={16} />}
                                                label={`por ${createdBy}`}
                                                variant="outlined"
                                                sx={{ height: 24 }}
                                            />
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Lucide.Clock size={14} style={{ opacity: 0.6 }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Criado em {fmt(createdAt)}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="caption" color="text.disabled">•</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Atualizado em {fmt(updatedAt)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    <CardActions sx={{ p: 0 }}>
                                        <Tooltip title="Documento (conteúdo)">
                                            <IconButton
                                                onClick={() => {
                                                    if (onOpenEditor) onOpenEditor(tpl);
                                                    else onEdit?.({ ...tpl, openEditor: true });
                                                }}
                                            >
                                                <Lucide.NotebookPen />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Gerar PDF (stub)">
                                            <IconButton onClick={() => onExportPdf?.(tpl)}><Lucide.FileType /></IconButton>
                                        </Tooltip>

                                        <Tooltip title="Duplicar">
                                            <IconButton onClick={() => onDuplicate?.(tpl)}><Lucide.Copy /></IconButton>
                                        </Tooltip>

                                        <Tooltip title="Editar metadados">
                                            <IconButton onClick={() => onEdit?.(tpl)}><Lucide.Pencil /></IconButton>
                                        </Tooltip>

                                        <Tooltip title="Excluir">
                                            <IconButton color="error" onClick={() => onDelete?.(tpl)}><Lucide.Trash2 /></IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Stack>
                            </CardContent>
                            <Divider />
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
}
